import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Paper,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import ProjectService from '../../services/project.service';

const ProjectList: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await ProjectService.getAll({
        populate: '*',
        'pagination[pageSize]': 100,
      });
      setProjects(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      planning: 'default',
      active: 'success',
      onHold: 'warning',
      completed: 'info',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Projects</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/projects/create')}
        >
          New Project
        </Button>
      </Stack>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Stack direction="row" flexWrap="wrap" gap={3}>
          {projects.map((project) => (
            <Box key={project.id} sx={{ width: { xs: '100%', md: 'calc(50% - 12px)', lg: 'calc(33.33% - 16px)' } }}>
              <Card
                sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" noWrap>
                        {project.attributes?.projectName}
                      </Typography>
                      <Chip
                        label={project.attributes?.status}
                        size="small"
                        color={getStatusColor(project.attributes?.status)}
                      />
                    </Stack>

                    <Typography variant="body2" color="text.secondary" noWrap>
                      Code: {project.attributes?.projectCode}
                    </Typography>

                    <Box>
                      <Stack direction="row" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">Progress</Typography>
                        <Typography variant="body2">
                          {project.attributes?.progress || 0}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={project.attributes?.progress || 0}
                      />
                    </Box>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Budget: ${project.attributes?.budget?.toFixed(2) || '0.00'}
                      </Typography>
                      <Chip
                        label={project.attributes?.priority}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Stack>
      )}

      {!loading && projects.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No projects found
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ProjectList;

